import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Clock3, MapPin, Phone } from "lucide-react";
import Nav from "./Nav";
import QuickActions from "./QuickActions";
import { PHONE } from "./shared";

const POSTS = [
  {
    slug: "kent-ro-services",
    tag: "RO Care Guide",
    date: "Aug 2026",
    title: "Kent RO Services in Raipur —",
    em: "Sales, Installation & Repair",
    excerpt: "New Kent RO sales, professional installation, repairs, genuine filter & candle replacement, and AMC plans — prompt doorstep service across Raipur.",
    seoTitle: "Kent RO Services in Raipur | Parth Enterprises",
    lead: "Welcome to Kent RO Sales and Services — your trusted partner for pure, safe, and healthy drinking water.",
    blocks: [
      { h2: "Pure water, better health" },
      { p: "At Kent RO Sales and Services, we believe that clean water is the foundation of a healthy life. With water contamination on the rise, having an advanced purification system in your home or office isn't just a luxury — it's a necessity." },
      { p: "We are your one-stop solution for India's most trusted water purification technology. From expert purchase guidance to prompt maintenance and repairs, we ensure your family drinks only the purest water every single day." },
      { h2: "Our professional services" },
      { p: "We offer comprehensive, end-to-end solutions for all models of Kent RO water purifiers:" },
      { services: [
        { title: "New RO Sales", text: "Confused about which model best suits your water quality and family size? Our experts help you choose the right Kent RO system at the best price." },
        { title: "Professional Installation", text: "Hassle-free, precise installation by certified technicians to ensure optimal performance from day one." },
        { title: "Repair & Troubleshooting", text: "Strange tastes, leakage, low water flow or error codes? We diagnose and fix problems quickly." },
        { title: "Filter & Candle Replacement", text: "Regular maintenance with 100% genuine Kent spare parts to maintain water quality and extend the life of your machine." },
        { title: "Annual Maintenance Contracts (AMC)", text: "Complete peace of mind with cost-effective AMC plans — scheduled servicing and priority breakdown support." },
        { title: "Uninstallation & Relocation", text: "Moving homes? Our experts safely uninstall and reinstall your RO system at your new location." },
      ]},
      { h2: "Why choose us?" },
      { checklist: [
        ["Certified Technicians", "Highly trained and experienced professionals for reliable service."],
        ["Genuine Spare Parts", "Only authentic Kent components — protecting your machine's warranty and efficiency."],
        ["Prompt Doorstep Service", "Quick turnaround times so you never wait long for clean water."],
        ["Transparent Pricing", "No hidden charges — just honest, affordable rates."],
        ["Customer Satisfaction Guaranteed", "We prioritise your health and happiness above all else."],
      ]},
      { h2: "Protect your family today" },
      { p: "Don't compromise on your family's health. Whether you need a brand-new RO system installed or your current purifier serviced, we are just a phone call away." },
      { cta: true },
      { signoff: "Kent RO Sales and Services — purity you can trust, service you can rely on." },
    ],
  },
  {
    slug: "aquaguard-ro-services",
    tag: "RO Care Guide",
    date: "Aug 2026",
    title: "Aquaguard RO Services in Raipur —",
    em: "Sales, Installation & Repair",
    excerpt: "New Aquaguard RO sales, expert repairs, filter replacement and AMC plans — certified technicians, genuine parts, doorstep service across Raipur.",
    seoTitle: "Aquaguard RO Services in Raipur | Parth Enterprises",
    lead: "Pure water, healthy life — your trusted partner for advanced water purification solutions.",
    blocks: [
      { h2: "Welcome to Aquaguard RO Sales & Services" },
      { p: "Access to clean, safe, and great-tasting drinking water is essential for your family's health and well-being. At Aquaguard RO Sales and Services, we bring you India's most trusted water purification technology right to your doorstep." },
      { p: "Whether you are looking to purchase a brand-new state-of-the-art RO system, need routine maintenance, or require urgent repairs, our team of certified professionals is here to ensure your water is pure and safe — every single drop." },
      { h2: "Why choose us?" },
      { checklist: [
        ["Certified Technicians", "Highly trained experts with years of experience handling all models of Aquaguard water purifiers."],
        ["Genuine Spare Parts", "100% authentic filters, membranes and parts for the longevity and efficiency of your machine."],
        ["Prompt Doorstep Service", "Quick response times for installation, service and repairs, right at your home or office."],
        ["Affordable Pricing", "Transparent pricing with no hidden costs on sales, service and annual maintenance contracts."],
        ["Customer-Centric Approach", "Your health and satisfaction are our top priorities."],
      ]},
      { h2: "Our comprehensive services" },
      { p: "We offer end-to-end solutions for all your water purification needs:" },
      { services: [
        { title: "New RO Sales", text: "Borewell, tanker or municipal supply — our experts help you choose the ideal Aquaguard RO model for your water quality and family size, with free doorstep delivery and professional installation." },
        { title: "Expert Repair & Troubleshooting", text: "Purifier leaking, making strange noises, or giving low water flow? Rapid diagnosis and repair so you never compromise on health." },
        { title: "Routine Maintenance & Filter Replacement", text: "Timely servicing, sediment filter cleaning, carbon filter replacement and TDS adjustments to keep your water crystal clear." },
        { title: "Annual Maintenance Contracts (AMC)", text: "Complete peace of mind — scheduled periodic maintenance, free filter replacements and priority breakdown support all year." },
      ]},
      { p: "Common issues we fix:" },
      { bullets: ["PUMP and SMPS failures", "Auto-shut-off issues", "Unusual taste or odor in purified water", "Clogged filters and membranes"] },
      { h2: "Our popular technologies" },
      { p: "We deal in advanced multi-stage purification systems featuring:" },
      { services: [
        { title: "RO (Reverse Osmosis)", text: "Removes dissolved impurities, heavy metals and hard water salts." },
        { title: "UV (Ultraviolet)", text: "Neutralises waterborne disease-causing bacteria and viruses instantly." },
        { title: "UF (Ultra Filtration)", text: "Physical filtration to remove turbidity and suspended particles." },
        { title: "Mineral Guard / Taste Adjuster", text: "Retains essential natural minerals while enhancing the taste of water." },
      ]},
      { h2: "Get in touch with us today!" },
      { p: "Don't let contaminated water put your family's health at risk. For new installations, expert servicing, or emergency repairs, speak directly with our water purification experts." },
      { cta: true },
      { info: [
        ["Working Hours", "Monday – Sunday · 8:00 AM – 9:00 PM"],
        ["Service Area", "Raipur — Bandhwapara, Purani Basti & nearby areas"],
      ]},
      { signoff: "Aquaguard RO Sales and Services — pure water, healthy life." },
    ],
  },
  {
    slug: "ro-water-purifier-service-near-me",
    tag: "Service Guide",
    date: "Sep 2026",
    title: "Expert RO Water Purifier Service Near You —",
    em: "Fast, Reliable & Affordable",
    excerpt: "Doorstep RO repair, filter replacement and maintenance in Raipur within 60 minutes — certified technicians, genuine parts, honest pricing.",
    seoTitle: "RO Water Purifier Service Near Me in Raipur | Parth Enterprises",
    lead: "Get doorstep RO repair, filter replacement, and regular maintenance from certified technicians within 60 minutes. Serving Raipur and surrounding areas.",
    blocks: [
      { cta: true },
      { h2: "Why choose our local RO service?" },
      { p: "Don't compromise on your family's health. When you search for \"RO water purifier service near me\", you want someone quick, trustworthy and expert. Here is why homeowners across Raipur trust Parth Enterprises:" },
      { checklist: [
        ["Lightning-Fast Response", "Our local technicians are stationed across Raipur to ensure service within 1 hour of your call."],
        ["Certified & Experienced Technicians", "Trained to service all major brands including Kent, Aquaguard, Eureka Forbes, AO Smith, Livpure and more."],
        ["Genuine Spare Parts", "100% authentic filters, membranes and UV lamps with warranty for long-lasting performance."],
        ["Transparent & Honest Pricing", "No hidden fees — get upfront quotes before any repair work begins."],
        ["Service Warranty", "Peace of mind with our 30-day service warranty on all repairs and part replacements."],
      ]},
      { h2: "Comprehensive RO water purifier services we offer" },
      { p: "Whether your purifier is making strange noises, leaking, or delivering bad-tasting water, we provide end-to-end solutions:" },
      { services: [
        { title: "RO Repair & Troubleshooting", text: "Fixing electrical faults, pump issues, leakage and auto-shut-off problems." },
        { title: "Filter & Cartridge Replacement", text: "Timely replacement of sediment filters, carbon filters and RO/UF membranes." },
        { title: "Periodic Maintenance & Servicing", text: "Comprehensive check-ups to ensure your system runs at peak efficiency." },
        { title: "TDS Adjustment", text: "Calibrating your purifier to maintain the optimal balance of essential minerals and pure water." },
        { title: "Installation & Uninstallation", text: "Moving houses? Let our experts safely uninstall and reinstall your RO system." },
      ]},
      { h2: "Signs your RO purifier needs immediate service" },
      { p: "Is your water purifier trying to tell you something? Call us if you notice:" },
      { checklist: [
        ["Slow Water Flow", "The purification process is taking much longer than usual."],
        ["Bad Taste or Odor", "The water tastes metallic, salty or strange."],
        ["Water Leaks", "Pooling water around or under the purifier unit."],
        ["Strange Noises", "Loud humming or rattling sounds from the booster pump."],
        ["Indicator Lights", "Red warning lights signaling filter change or system error."],
      ]},
      { h2: "How it works — simple 3-step process" },
      { steps: [
        ["Book Your Service", "Call us or fill out our quick online booking form with your location in Raipur."],
        ["Doorstep Diagnosis", "Our certified technician arrives at your doorstep at your scheduled time for a thorough inspection."],
        ["Quick Repair & Pure Water", "We fix the issue transparently on-site, ensuring your water is safe and clean once again."],
      ]},
      { h2: "Brands we service" },
      { p: "We provide expert repair and maintenance for all leading residential and commercial water purifier brands:" },
      { bullets: ["Kent", "Aquaguard (Eureka Forbes)", "AO Smith", "Livpure", "Blue Star", "Pureit", "And all other domestic RO/UV/UF brands!"] },
      { h2: "What our local customers say" },
      { quotes: [
        ["“My RO stopped working suddenly on a Sunday morning. I searched for 'RO service near me' and found Parth Enterprises. A technician arrived in 45 minutes and fixed the issue professionally. Highly recommended!”", "Priya S., Telibandha"],
        ["“Very prompt and polite service. They replaced my filters at a very reasonable price compared to the brand's direct service center.”", "Rohit K., Shankar Nagar"],
      ]},
      { h2: "Frequently asked questions" },
      { faqs: [
        ["How often should I service my RO water purifier?", "We recommend servicing your RO system every 3 to 6 months, depending on your local water quality and usage, to ensure safe drinking water and extend the life of the machine."],
        ["Do you provide a warranty on repairs?", "Yes! We offer a 30-day warranty on all repairs and newly replaced spare parts."],
        ["How quickly can a technician reach my home?", "Because our technicians are locally based across Raipur, we typically reach your doorstep within 60 minutes of booking."],
        ["What areas do you cover for RO service?", "We cover all major localities across Raipur — Shankar Nagar, Civil Lines, Telibandh, Pandri, Naya Raipur and surrounding areas. Call us to confirm immediate availability!"],
      ]},
      { h2: "Ready for pure, safe drinking water?" },
      { p: "Don't let contaminated water put your family at risk. Book the most reliable RO water purifier service near you today!" },
      { cta: true },
      { signoff: "Parth Enterprises — fast, reliable RO service near you, Raipur." },
    ],
  },
];

function Block({ b }) {
  if (b.h2) return <h2>{b.h2}</h2>;
  if (b.p) return <p>{b.p}</p>;
  if (b.services) return <div className="blog-services">{b.services.map((s) => <div key={s.title}><h3>{s.title}</h3><p>{s.text}</p></div>)}</div>;
  if (b.checklist) return <ul className="blog-checklist">{b.checklist.map(([t, x]) => <li key={t}><Check size={17}/><span><b>{t}:</b> {x}</span></li>)}</ul>;
  if (b.bullets) return <ul className="blog-checklist">{b.bullets.map((t) => <li key={t}><Check size={17}/><span>{t}</span></li>)}</ul>;
  if (b.steps) return <div className="blog-steps">{b.steps.map(([t, x], i) => <div key={t}><b>{i + 1}</b><div><h3>{t}</h3><p>{x}</p></div></div>)}</div>;
  if (b.quotes) return <div className="blog-quotes">{b.quotes.map(([text, author]) => <blockquote key={author}>{text}<footer>— {author}</footer></blockquote>)}</div>;
  if (b.faqs) return <div className="blog-faq">{b.faqs.map(([q, a]) => <details key={q}><summary>{q}</summary><p>{a}</p></details>)}</div>;
  if (b.cta) return (
    <div className="blog-cta">
      <h3>Book a service today</h3>
      <p>Call {PHONE} to book a service or speak with our water purification experts.</p>
      <div className="blog-cta-actions">
        <a className="btn btn-primary" href={`tel:${PHONE}`} data-testid="blog-call-button"><Phone size={16}/> Call {PHONE}</a>
        <a className="btn btn-ghost" href="/#contact" data-testid="blog-book-online">Book a Service Online <ArrowRight size={15}/></a>
      </div>
    </div>
  );
  if (b.info) return (
    <div className="blog-info">
      {b.info.map(([label, value]) => (
        <div key={label}>{label === "Working Hours" ? <Clock3 size={16}/> : <MapPin size={16}/>}<span>{label}<b>{value}</b></span></div>
      ))}
    </div>
  );
  if (b.signoff) return <p className="blog-signoff">{b.signoff}</p>;
  return null;
}

export function BlogList() {
  useEffect(() => { document.title = "Blog — Parth Enterprises, RO Sales & Service Raipur"; window.scrollTo(0, 0); }, []);
  return (
    <main className="blog-page">
      <Nav/>
      <section className="blog-list container">
        <p className="kicker"><span/> Blog</p>
        <h1 className="section-title">Water wisdom &<br/><em>service guides.</em></h1>
        <div className="blog-cards">
          {POSTS.map((p) => (
            <Link data-testid={`blog-card-${p.slug}`} className="blog-card" to={`/blog/${p.slug}`} key={p.slug}>
              <span className="blog-card-tag">{p.tag} · {p.date}</span>
              <h2>{p.title} {p.em}</h2>
              <p>{p.excerpt}</p>
              <span className="text-link">Read article <ArrowRight size={15}/></span>
            </Link>
          ))}
        </div>
      </section>
      <QuickActions/>
    </main>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  useEffect(() => {
    document.title = post ? post.seoTitle : "Blog — Parth Enterprises";
    window.scrollTo(0, 0);
  }, [post]);
  if (!post) {
    return (
      <main className="blog-page">
        <Nav/>
        <div className="blog-article container"><p className="blog-lead">Article not found.</p><Link to="/blog" className="text-link"><ArrowLeft size={15}/> All articles</Link></div>
      </main>
    );
  }
  return (
    <main className="blog-page">
      <Nav/>
      <article className="blog-article container" data-testid="blog-article">
        <Link to="/blog" className="text-link" data-testid="blog-back-link"><ArrowLeft size={15}/> All articles</Link>
        <p className="kicker"><span/> {post.tag} · {post.date}</p>
        <h1>{post.title} <em>{post.em}</em></h1>
        <p className="blog-lead">{post.lead}</p>
        <a className="btn btn-primary" href={`tel:${PHONE}`}><Phone size={16}/> Call Now: {PHONE}</a>
        {post.blocks.map((b, i) => <Block key={i} b={b}/>)}
      </article>
      <QuickActions/>
    </main>
  );
}
